interface MercadoPagoBasisTheoryPayment {
  id: string;
  paymentEngine: 'mercadopago';
  paymentType: 'credit_card';
  status: string;
  purchaseState?: string;
  using3dSecure: boolean;
}

type MercadoPagoBasisTheoryPollingReason =
  | 'invalid_options'
  | 'invalid_response'
  | 'request_failed'
  | 'timeout'
  | 'aborted';

class MercadoPagoBasisTheoryPollingError extends Error {
  readonly reason: MercadoPagoBasisTheoryPollingReason;

  readonly retryable: boolean;

  constructor(reason: MercadoPagoBasisTheoryPollingReason, retryable = false) {
    super(`mercadopago_bt_polling_${reason}`);
    this.name = 'MercadoPagoBasisTheoryPollingError';
    this.reason = reason;
    this.retryable = retryable;
  }
}

interface MercadoPagoBasisTheoryPollingOptions {
  // eslint-disable-next-line no-unused-vars -- This is a type-only callback parameter.
  loadPayment: (signal: AbortSignal) => Promise<unknown>;
  expectedPaymentId?: string;
  signal: AbortSignal;
  // eslint-disable-next-line no-unused-vars -- This is a type-only callback parameter.
  onPayment?: (payment: MercadoPagoBasisTheoryPayment) => void;
  maxAttempts?: number;
  intervalMs?: number;
  timeoutMs?: number;
  requestTimeoutMs?: number;
}

const paymentStatuses = new Set([
  'attempt',
  'pending',
  'charged',
  'failed',
  'rejected',
  'suspicious',
  'canceled',
  'chargeback',
  'unsafe',
  'fully_refunded',
  'partially_refunded',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Allowlist public payment metadata; never retain challenge credentials or provider errors. */
function normalizeMercadoPagoBasisTheoryPayment(
  value: unknown,
): MercadoPagoBasisTheoryPayment | undefined {
  if (!isRecord(value)) return undefined;
  const payment = 'payload' in value ? value.payload : value;
  if (!isRecord(payment)) return undefined;

  const engine = payment.paymentEngine ?? payment.payment_engine;
  const type = payment.paymentType ?? payment.payment_type;
  const id =
    typeof payment.id === 'number' && Number.isSafeInteger(payment.id) && payment.id > 0
      ? String(payment.id)
      : payment.id;
  const purchaseState = payment.purchaseState ?? payment.purchase_state;
  const using3dSecure = payment.using3dSecure ?? payment.using_3d_secure;
  if (
    engine !== 'mercadopago' ||
    type !== 'credit_card' ||
    typeof id !== 'string' ||
    !id.trim() ||
    typeof payment.status !== 'string' ||
    !paymentStatuses.has(payment.status) ||
    (purchaseState !== undefined && typeof purchaseState !== 'string') ||
    (using3dSecure !== undefined && typeof using3dSecure !== 'boolean')
  ) {
    return undefined;
  }

  return {
    id: id.trim(),
    paymentEngine: engine,
    paymentType: type,
    status: payment.status,
    ...(typeof purchaseState === 'string' ? { purchaseState } : {}),
    using3dSecure: using3dSecure === true,
  };
}

/** Bound a read, abort its transport and retain only safe failure categories. */
function awaitMercadoPagoBasisTheoryRead(
  // eslint-disable-next-line no-unused-vars -- This is a type-only callback parameter.
  start: (signal: AbortSignal) => Promise<unknown>,
  signal: AbortSignal,
  timeoutMs: number,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const requestController = new AbortController();
    let settled = false;
    let deadline: ReturnType<typeof setTimeout>;
    function finish(
      reason?: MercadoPagoBasisTheoryPollingReason,
      value?: unknown,
      retryable = false,
    ) {
      if (settled) return;
      settled = true;
      clearTimeout(deadline);
      signal.removeEventListener('abort', abort);
      if (reason) {
        requestController.abort();
        reject(new MercadoPagoBasisTheoryPollingError(reason, retryable));
      } else resolve(value);
    }
    function abort() {
      finish('aborted');
    }
    if (signal.aborted) {
      abort();
      return;
    }

    signal.addEventListener('abort', abort, { once: true });
    deadline = setTimeout(() => {
      finish('timeout', undefined, true);
    }, timeoutMs);
    try {
      start(requestController.signal).then(
        (value) => {
          finish(undefined, value);
        },
        (error: unknown) => {
          if (error instanceof MercadoPagoBasisTheoryPollingError) {
            finish(error.reason, undefined, error.retryable);
          } else {
            finish('request_failed', undefined, error instanceof TypeError);
          }
        },
      );
    } catch (error: unknown) {
      finish('request_failed', undefined, error instanceof TypeError);
    }
  });
}

/** Wait for an interval and release its timer and cancellation listener. */
function sleepMercadoPagoBasisTheory(signal: AbortSignal, durationMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout>;
    function finish() {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      resolve();
    }
    function abort() {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      reject(new MercadoPagoBasisTheoryPollingError('aborted'));
    }
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
    timer = setTimeout(finish, durationMs);
  });
}

function assertPollingActive(signal: AbortSignal): void {
  if (signal.aborted) throw new MercadoPagoBasisTheoryPollingError('aborted');
}

/** Reconcile an existing charge using read requests only, never overlapping requests. */
async function pollMercadoPagoBasisTheoryPayment({
  loadPayment,
  expectedPaymentId,
  signal,
  onPayment,
  maxAttempts = 40,
  intervalMs = 3000,
  timeoutMs = 120000,
  requestTimeoutMs = 30000,
}: MercadoPagoBasisTheoryPollingOptions): Promise<MercadoPagoBasisTheoryPayment> {
  if (
    !Number.isSafeInteger(maxAttempts) ||
    maxAttempts <= 0 ||
    !Number.isFinite(intervalMs) ||
    intervalMs < 0 ||
    !Number.isFinite(timeoutMs) ||
    timeoutMs <= 0 ||
    !Number.isFinite(requestTimeoutMs) ||
    requestTimeoutMs <= 0 ||
    [intervalMs, timeoutMs, requestTimeoutMs].some((duration) => duration > 2147483647)
  ) {
    throw new MercadoPagoBasisTheoryPollingError('invalid_options');
  }

  const deadline = Date.now() + timeoutMs;
  let paymentId = expectedPaymentId;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    assertPollingActive(signal);
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) throw new MercadoPagoBasisTheoryPollingError('timeout');

    try {
      const response = await awaitMercadoPagoBasisTheoryRead(
        loadPayment,
        signal,
        Math.min(requestTimeoutMs, remainingMs),
      );
      assertPollingActive(signal);
      if (Date.now() >= deadline) throw new MercadoPagoBasisTheoryPollingError('timeout');
      const payment = normalizeMercadoPagoBasisTheoryPayment(response);
      if (!payment || (paymentId !== undefined && paymentId !== payment.id)) {
        throw new MercadoPagoBasisTheoryPollingError('invalid_response');
      }
      paymentId = payment.id;
      onPayment?.(payment);
      assertPollingActive(signal);

      // Approval can precede ticket issuance. Do not treat a charged attempt as a failure.
      const awaitingPurchase = payment.status === 'charged' && payment.purchaseState === 'attempt';
      if (payment.status !== 'attempt' && payment.status !== 'pending' && !awaitingPurchase) {
        return payment;
      }
    } catch (error: unknown) {
      assertPollingActive(signal);
      if (!(error instanceof MercadoPagoBasisTheoryPollingError) || !error.retryable) throw error;
    }
    if (attempt + 1 === maxAttempts) break;

    const waitMs = Math.min(intervalMs, deadline - Date.now());
    if (waitMs <= 0 && intervalMs > 0) break;
    await sleepMercadoPagoBasisTheory(signal, Math.max(0, waitMs));
  }
  throw new MercadoPagoBasisTheoryPollingError('timeout');
}

export type { MercadoPagoBasisTheoryPayment, MercadoPagoBasisTheoryPollingOptions };
export {
  MercadoPagoBasisTheoryPollingError,
  awaitMercadoPagoBasisTheoryRead,
  sleepMercadoPagoBasisTheory,
  normalizeMercadoPagoBasisTheoryPayment,
  pollMercadoPagoBasisTheoryPayment,
};
